<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcInvoiceImportNotificationCard',
    inheritAttrs: false,
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    notification: {
      type: Object,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const ux = ref([]);
  const b_1468474219929533_style = computed(() => ({
    maxWidth: '200px',
  }));
  const b_8385232899523842_style = computed(() => ({
    maxWidth: '200px',
  }));

  const $data = computed(() => ({
    ux: ux.value,
    b_1468474219929533_style: b_1468474219929533_style.value,
    b_8385232899523842_style: b_8385232899523842_style.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'ux' },
      { name: 'b_1468474219929533_style', nodeId: '1468474219929533' },
      { name: 'b_8385232899523842_style', nodeId: '8385232899523842' },
    ],
    loaders: {},
    ux: {},
    dataRefs: { ux, b_1468474219929533_style, b_8385232899523842_style },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const notificationType = computed(() => {
    return _.last(props.notification.type.split(':'));
  });

  watch(
    () => props.notification,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('notification');
    }
  );

  const actions = {};

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpBox direction="row" alignItems="start" class="flex-fill">
    <div class="me-3 pt-1">
      <Icon
        icon="ri:import-line"
        :tooltip="pgettext('Notification', 'Invoice import')"
      >
      </Icon>
    </div>
    <ItpBox direction="column" class="flex-fill">
      <ItpBox direction="row">
        <ItpText
          :text="pgettext('Notification', 'Invoice import')"
          small
          :title="notification.description"
        >
        </ItpText>
      </ItpBox>
      <ItpBox direction="row">
        <ItpText
          :title="notification.data.company.name"
          strong
          :text="notification.data.company.name"
          class="me-1"
          :style="b_1468474219929533_style"
        >
        </ItpText>
        <ItpLink
          :icon="$config.externalLinkIcon"
          :to="{
            name: 'app.company',
            params: { id: notification.data.company.id },
          }"
          v-if="notification.data.company.id"
        >
        </ItpLink>
        <span class="mx-2"> &middot; </span>
        <UcInvoiceKindBadge
          :value="notification.data.invoice_kind"
          v-if="notification.data.invoice_kind"
        >
        </UcInvoiceKindBadge>
        <ItpText strong :text="notification.data.invoice_number" class="me-1">
        </ItpText>
        <ItpLink
          :icon="$config.externalLinkIcon"
          :to="{
            name: 'app.invoice',
            params: { id: notification.key },
          }"
          v-if="notification.data.invoice_id"
        >
        </ItpLink>
        <span class="mx-2"> &middot; </span>
        <ItpText
          :title="notification.data.client.name"
          strong
          :text="notification.data.client.name"
          class="me-1"
          :style="b_8385232899523842_style"
        >
        </ItpText>
        <ItpLink
          :icon="$config.externalLinkIcon"
          :to="{
            name: 'app.company',
            params: { id: notification.data.client.id },
          }"
          v-if="notification.data.client.id"
        >
        </ItpLink>
      </ItpBox>
      <div class="mt-1" v-if="notification.data.level === 'warning'">
        <ItpBadge variant="warning" class="me-2">
          {{ notification.description }}
        </ItpBadge>
      </div>
    </ItpBox>
    <ItpBox class="align-self-center">
      <ItpText small muted :title="notification.key">
        {{ datetime(notification.created_at) }}
        {{ username(notification.created_by) }}
      </ItpText>
    </ItpBox>
  </ItpBox>
</template>
